/**
 * GeometryEditor Service
 *
 * Provides pure geometry manipulation methods for node-level editing of
 * GeoJSON geometries. Supports Point, LineString, Polygon, and their
 * Multi variants.
 *
 * **Validates: Requirements 16.1, 16.2, 16.3, 16.4**
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A 2D coordinate pair [longitude, latitude] or [x, y]. */
export type Coordinate = [number, number];

/** Supported GeoJSON geometry types for node editing. */
export type EditableGeometryType =
  | 'Point'
  | 'LineString'
  | 'Polygon'
  | 'MultiPoint'
  | 'MultiLineString'
  | 'MultiPolygon';

/** GeoJSON Geometry interface (simplified for our use). */
export interface Geometry {
  type: EditableGeometryType;
  coordinates: unknown;
}

/** Result of a node editing operation. */
export interface EditResult {
  success: boolean;
  geometry: Geometry | null;
  error?: string;
}

// ---------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------

/**
 * Flattens all coordinates from a geometry into a flat array of Coordinate pairs.
 * Returns coordinates in traversal order.
 */
function flattenCoordinates(geometry: Geometry): Coordinate[] {
  switch (geometry.type) {
    case 'Point':
      return [geometry.coordinates as Coordinate];

    case 'MultiPoint':
    case 'LineString':
      return (geometry.coordinates as Coordinate[]).map((c) => [c[0], c[1]]);

    case 'Polygon':
    case 'MultiLineString': {
      const rings = geometry.coordinates as Coordinate[][];
      return rings.flatMap((ring) => ring.map((c) => [c[0], c[1]] as Coordinate));
    }

    case 'MultiPolygon': {
      const polygons = geometry.coordinates as Coordinate[][][];
      return polygons.flatMap((polygon) =>
        polygon.flatMap((ring) => ring.map((c) => [c[0], c[1]] as Coordinate))
      );
    }

    default:
      return [];
  }
}

/**
 * Counts the total number of nodes in a geometry.
 */
function countNodes(geometry: Geometry): number {
  return flattenCoordinates(geometry).length;
}

/**
 * Deep clones a geometry object.
 */
function cloneGeometry(geometry: Geometry): Geometry {
  return JSON.parse(JSON.stringify(geometry));
}

// ---------------------------------------------------------------------------
// GeometryEditor Service
// ---------------------------------------------------------------------------

/**
 * Extracts all vertices from a geometry as an array of Coordinate pairs.
 *
 * For Polygons, the closing coordinate (which duplicates the first) is included.
 * Nodes are returned in traversal order across all rings/parts.
 *
 * @param geometry - The GeoJSON geometry to extract nodes from
 * @returns Array of [x, y] coordinate pairs
 */
export function getNodes(geometry: Geometry): Coordinate[] {
  return flattenCoordinates(geometry);
}

/**
 * Adds a new node (vertex) at a specified position on a segment of the geometry.
 *
 * For LineString: inserts the coordinate after the given segmentIndex.
 * For Polygon: inserts the coordinate after the given segmentIndex in the
 *   appropriate ring.
 * For Point/MultiPoint: adds a new point at the given position.
 *
 * @param geometry - The source geometry
 * @param segmentIndex - The index of the segment to split (0-based).
 *   For a LineString with N points, valid segment indices are 0..N-2.
 *   For a Polygon ring, the segment connects node[i] to node[i+1].
 * @param position - The [x, y] coordinate to insert
 * @returns EditResult with the modified geometry
 */
export function addNode(
  geometry: Geometry,
  segmentIndex: number,
  position: Coordinate
): EditResult {
  const cloned = cloneGeometry(geometry);

  switch (cloned.type) {
    case 'Point': {
      // Cannot add a node to a Point; convert to MultiPoint
      return {
        success: false,
        geometry: null,
        error: 'Cannot add a node to a Point geometry. Use MultiPoint instead.',
      };
    }

    case 'MultiPoint': {
      const coords = cloned.coordinates as Coordinate[];
      if (segmentIndex < 0 || segmentIndex > coords.length) {
        return { success: false, geometry: null, error: 'Segment index out of bounds' };
      }
      coords.splice(segmentIndex + 1, 0, position);
      return { success: true, geometry: cloned };
    }

    case 'LineString': {
      const coords = cloned.coordinates as Coordinate[];
      const maxSegment = coords.length - 2;
      if (segmentIndex < 0 || segmentIndex > maxSegment) {
        return {
          success: false,
          geometry: null,
          error: `Segment index out of bounds. Valid range: 0-${maxSegment}`,
        };
      }
      coords.splice(segmentIndex + 1, 0, position);
      return { success: true, geometry: cloned };
    }

    case 'Polygon': {
      const rings = cloned.coordinates as Coordinate[][];
      // Find which ring and local segment index
      let remaining = segmentIndex;
      for (let ringIdx = 0; ringIdx < rings.length; ringIdx++) {
        const ring = rings[ringIdx];
        // A closed ring has ring.length - 1 segments
        const segmentCount = ring.length - 1;
        if (remaining < segmentCount) {
          // Insert after remaining index
          ring.splice(remaining + 1, 0, position);
          // If we inserted at the last segment position, update closing coordinate
          // The ring must remain closed (first == last)
          if (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1]) {
            ring[ring.length - 1] = [ring[0][0], ring[0][1]];
          }
          return { success: true, geometry: cloned };
        }
        remaining -= segmentCount;
      }
      return { success: false, geometry: null, error: 'Segment index out of bounds for polygon' };
    }

    case 'MultiLineString': {
      const lines = cloned.coordinates as Coordinate[][];
      let remaining = segmentIndex;
      for (let lineIdx = 0; lineIdx < lines.length; lineIdx++) {
        const line = lines[lineIdx];
        const segmentCount = line.length - 1;
        if (remaining < segmentCount) {
          line.splice(remaining + 1, 0, position);
          return { success: true, geometry: cloned };
        }
        remaining -= segmentCount;
      }
      return {
        success: false,
        geometry: null,
        error: 'Segment index out of bounds for MultiLineString',
      };
    }

    case 'MultiPolygon': {
      const polygons = cloned.coordinates as Coordinate[][][];
      let remaining = segmentIndex;
      for (let polyIdx = 0; polyIdx < polygons.length; polyIdx++) {
        const rings = polygons[polyIdx];
        for (let ringIdx = 0; ringIdx < rings.length; ringIdx++) {
          const ring = rings[ringIdx];
          const segmentCount = ring.length - 1;
          if (remaining < segmentCount) {
            ring.splice(remaining + 1, 0, position);
            // Maintain ring closure
            if (
              ring[0][0] !== ring[ring.length - 1][0] ||
              ring[0][1] !== ring[ring.length - 1][1]
            ) {
              ring[ring.length - 1] = [ring[0][0], ring[0][1]];
            }
            return { success: true, geometry: cloned };
          }
          remaining -= segmentCount;
        }
      }
      return {
        success: false,
        geometry: null,
        error: 'Segment index out of bounds for MultiPolygon',
      };
    }

    default:
      return { success: false, geometry: null, error: `Unsupported geometry type: ${cloned.type}` };
  }
}

/**
 * Removes a node (vertex) at the given index from the geometry.
 *
 * Validates that removal won't create an invalid geometry before proceeding.
 * - LineString requires at least 2 points
 * - Polygon rings require at least 4 points (3 unique + closing)
 *
 * @param geometry - The source geometry
 * @param nodeIndex - The 0-based index of the node to remove (in flattened order)
 * @returns EditResult with the modified geometry
 */
export function removeNode(geometry: Geometry, nodeIndex: number): EditResult {
  if (!isValidAfterRemoval(geometry, nodeIndex)) {
    return {
      success: false,
      geometry: null,
      error: 'Removing this node would create an invalid geometry',
    };
  }

  const cloned = cloneGeometry(geometry);

  switch (cloned.type) {
    case 'Point': {
      return {
        success: false,
        geometry: null,
        error: 'Cannot remove the only node from a Point geometry',
      };
    }

    case 'MultiPoint': {
      const coords = cloned.coordinates as Coordinate[];
      if (nodeIndex < 0 || nodeIndex >= coords.length) {
        return { success: false, geometry: null, error: 'Node index out of bounds' };
      }
      coords.splice(nodeIndex, 1);
      return { success: true, geometry: cloned };
    }

    case 'LineString': {
      const coords = cloned.coordinates as Coordinate[];
      if (nodeIndex < 0 || nodeIndex >= coords.length) {
        return { success: false, geometry: null, error: 'Node index out of bounds' };
      }
      coords.splice(nodeIndex, 1);
      return { success: true, geometry: cloned };
    }

    case 'Polygon': {
      const rings = cloned.coordinates as Coordinate[][];
      let remaining = nodeIndex;
      for (let ringIdx = 0; ringIdx < rings.length; ringIdx++) {
        const ring = rings[ringIdx];
        if (remaining < ring.length) {
          // For closed rings, if removing first or last node, handle closure
          const isFirst = remaining === 0;
          const isLast = remaining === ring.length - 1;

          if (isLast) {
            // Remove the closing duplicate and update closure
            ring.splice(remaining, 1);
            // Set new closing coordinate to match first
            ring[ring.length - 1] = [ring[0][0], ring[0][1]];
          } else if (isFirst) {
            // Remove first, then update the closing coordinate
            ring.splice(0, 1);
            ring[ring.length - 1] = [ring[0][0], ring[0][1]];
          } else {
            ring.splice(remaining, 1);
          }
          return { success: true, geometry: cloned };
        }
        remaining -= ring.length;
      }
      return { success: false, geometry: null, error: 'Node index out of bounds for polygon' };
    }

    case 'MultiLineString': {
      const lines = cloned.coordinates as Coordinate[][];
      let remaining = nodeIndex;
      for (let lineIdx = 0; lineIdx < lines.length; lineIdx++) {
        const line = lines[lineIdx];
        if (remaining < line.length) {
          line.splice(remaining, 1);
          return { success: true, geometry: cloned };
        }
        remaining -= line.length;
      }
      return {
        success: false,
        geometry: null,
        error: 'Node index out of bounds for MultiLineString',
      };
    }

    case 'MultiPolygon': {
      const polygons = cloned.coordinates as Coordinate[][][];
      let remaining = nodeIndex;
      for (let polyIdx = 0; polyIdx < polygons.length; polyIdx++) {
        const rings = polygons[polyIdx];
        for (let ringIdx = 0; ringIdx < rings.length; ringIdx++) {
          const ring = rings[ringIdx];
          if (remaining < ring.length) {
            const isFirst = remaining === 0;
            const isLast = remaining === ring.length - 1;

            if (isLast) {
              ring.splice(remaining, 1);
              ring[ring.length - 1] = [ring[0][0], ring[0][1]];
            } else if (isFirst) {
              ring.splice(0, 1);
              ring[ring.length - 1] = [ring[0][0], ring[0][1]];
            } else {
              ring.splice(remaining, 1);
            }
            return { success: true, geometry: cloned };
          }
          remaining -= ring.length;
        }
      }
      return {
        success: false,
        geometry: null,
        error: 'Node index out of bounds for MultiPolygon',
      };
    }

    default:
      return { success: false, geometry: null, error: `Unsupported geometry type: ${cloned.type}` };
  }
}

/**
 * Moves a node (vertex) at the given index to a new position.
 *
 * For Polygon geometries, if the moved node is the first or last in a ring
 * (which are duplicates for closure), both are updated to maintain ring closure.
 *
 * @param geometry - The source geometry
 * @param nodeIndex - The 0-based index of the node to move (in flattened order)
 * @param newPosition - The new [x, y] coordinate for the node
 * @returns EditResult with the modified geometry
 */
export function moveNode(
  geometry: Geometry,
  nodeIndex: number,
  newPosition: Coordinate
): EditResult {
  const totalNodes = countNodes(geometry);
  if (nodeIndex < 0 || nodeIndex >= totalNodes) {
    return { success: false, geometry: null, error: 'Node index out of bounds' };
  }

  const cloned = cloneGeometry(geometry);

  switch (cloned.type) {
    case 'Point': {
      cloned.coordinates = newPosition;
      return { success: true, geometry: cloned };
    }

    case 'MultiPoint':
    case 'LineString': {
      const coords = cloned.coordinates as Coordinate[];
      coords[nodeIndex] = newPosition;
      return { success: true, geometry: cloned };
    }

    case 'Polygon': {
      const rings = cloned.coordinates as Coordinate[][];
      let remaining = nodeIndex;
      for (let ringIdx = 0; ringIdx < rings.length; ringIdx++) {
        const ring = rings[ringIdx];
        if (remaining < ring.length) {
          ring[remaining] = newPosition;
          // Maintain ring closure
          const isFirst = remaining === 0;
          const isLast = remaining === ring.length - 1;
          if (isFirst) {
            ring[ring.length - 1] = [newPosition[0], newPosition[1]];
          } else if (isLast) {
            ring[0] = [newPosition[0], newPosition[1]];
          }
          return { success: true, geometry: cloned };
        }
        remaining -= ring.length;
      }
      return { success: false, geometry: null, error: 'Node index out of bounds for polygon' };
    }

    case 'MultiLineString': {
      const lines = cloned.coordinates as Coordinate[][];
      let remaining = nodeIndex;
      for (let lineIdx = 0; lineIdx < lines.length; lineIdx++) {
        const line = lines[lineIdx];
        if (remaining < line.length) {
          line[remaining] = newPosition;
          return { success: true, geometry: cloned };
        }
        remaining -= line.length;
      }
      return {
        success: false,
        geometry: null,
        error: 'Node index out of bounds for MultiLineString',
      };
    }

    case 'MultiPolygon': {
      const polygons = cloned.coordinates as Coordinate[][][];
      let remaining = nodeIndex;
      for (let polyIdx = 0; polyIdx < polygons.length; polyIdx++) {
        const rings = polygons[polyIdx];
        for (let ringIdx = 0; ringIdx < rings.length; ringIdx++) {
          const ring = rings[ringIdx];
          if (remaining < ring.length) {
            ring[remaining] = newPosition;
            const isFirst = remaining === 0;
            const isLast = remaining === ring.length - 1;
            if (isFirst) {
              ring[ring.length - 1] = [newPosition[0], newPosition[1]];
            } else if (isLast) {
              ring[0] = [newPosition[0], newPosition[1]];
            }
            return { success: true, geometry: cloned };
          }
          remaining -= ring.length;
        }
      }
      return {
        success: false,
        geometry: null,
        error: 'Node index out of bounds for MultiPolygon',
      };
    }

    default:
      return { success: false, geometry: null, error: `Unsupported geometry type: ${cloned.type}` };
  }
}

/**
 * Checks whether removing a node at the given index would produce a valid geometry.
 *
 * Validity rules:
 * - Point: cannot remove (always returns false)
 * - MultiPoint: must have at least 1 point remaining
 * - LineString: must have at least 2 points remaining
 * - MultiLineString: each line must have at least 2 points remaining
 * - Polygon: each ring must have at least 4 points remaining (3 unique + closing)
 * - MultiPolygon: each ring must have at least 4 points remaining
 *
 * @param geometry - The geometry to check
 * @param nodeIndex - The 0-based index of the node that would be removed
 * @returns true if the geometry would remain valid after removal
 */
export function isValidAfterRemoval(geometry: Geometry, nodeIndex: number): boolean {
  const totalNodes = countNodes(geometry);
  if (nodeIndex < 0 || nodeIndex >= totalNodes) {
    return false;
  }

  switch (geometry.type) {
    case 'Point':
      return false;

    case 'MultiPoint': {
      const coords = geometry.coordinates as Coordinate[];
      return coords.length > 1;
    }

    case 'LineString': {
      const coords = geometry.coordinates as Coordinate[];
      return coords.length > 2;
    }

    case 'Polygon': {
      const rings = geometry.coordinates as Coordinate[][];
      let remaining = nodeIndex;
      for (let ringIdx = 0; ringIdx < rings.length; ringIdx++) {
        const ring = rings[ringIdx];
        if (remaining < ring.length) {
          // A valid polygon ring needs at least 4 coordinates (3 unique + closing)
          // After removal it needs to still have 4
          return ring.length > 4;
        }
        remaining -= ring.length;
      }
      return false;
    }

    case 'MultiLineString': {
      const lines = geometry.coordinates as Coordinate[][];
      let remaining = nodeIndex;
      for (let lineIdx = 0; lineIdx < lines.length; lineIdx++) {
        const line = lines[lineIdx];
        if (remaining < line.length) {
          return line.length > 2;
        }
        remaining -= line.length;
      }
      return false;
    }

    case 'MultiPolygon': {
      const polygons = geometry.coordinates as Coordinate[][][];
      let remaining = nodeIndex;
      for (let polyIdx = 0; polyIdx < polygons.length; polyIdx++) {
        const rings = polygons[polyIdx];
        for (let ringIdx = 0; ringIdx < rings.length; ringIdx++) {
          const ring = rings[ringIdx];
          if (remaining < ring.length) {
            return ring.length > 4;
          }
          remaining -= ring.length;
        }
      }
      return false;
    }

    default:
      return false;
  }
}
